/**
 * Store Guide — Setup Wizard + User Manual for ClearRing
 *
 * ARCHITECTURE SYNC RULE (§12.5): Whenever ANY feature, field, page, toggle, or
 * workflow changes in ClearRing (new module, new tax type, new toggle, new FBR
 * scenario, new required field, new permission, new location setting, new report,
 * etc.), this file MUST be updated in BOTH the Setup Wizard steps AND the User
 * Manual feature cards to reflect that change.
 * File: frontend/src/pages/settings/TaxGuidePage.tsx
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, AlertCircle, Circle, RefreshCw, Building2,
  Percent, Package, Zap, Rocket, Loader2,
  BookOpen, ArrowRight, ShoppingCart, LayoutDashboard, Users,
  FileText, Warehouse, CreditCard, Receipt, ChevronRight,
  Settings, Landmark, Truck, Tag, BarChart3, Shield, Info, ExternalLink,
  FlaskConical, ShieldCheck, UtensilsCrossed, Factory,
  Star, Clock, Globe, Gift, Barcode, Calendar, Layers, WifiOff, DollarSign, RotateCcw,
  MapPin, Search, X, Link2, Printer,
} from 'lucide-react';
import { invalidateLocationQueries } from '@/lib/locations-cache';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/tab-bar';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth.store';
import { ProvincialRatesSection } from './ProvincialRatesSection';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { notify } from '@/lib/toast';
import {
  TAB_PARAM, buildCardLink, cardsFromParam, chipFromParam, cleanQuery, tabFromParam,
  type GuideTab,
} from './taxguide-url';
import { FilterChips } from '@/components/ui/filter-chips';
import { PoweredBy } from '@/components/print/PoweredBy';
import {
  FBR_SCENARIO_IDS,
  FBR_SCENARIO_NAMES,
  isDetailedScenario,
  scenarioCategory,
  type FbrScenarioCategory,
  type FbrScenarioId,
} from '@/lib/fbr-scenario-names';

type Status = 'complete' | 'incomplete' | 'optional' | 'na' | 'loading';

/** URL state of the guide (POS-I205): read validated values from the query string, write with replace. */
function useGuideParams() {
  const [params, setParams] = useSearchParams();
  const patch = useCallback((changes: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [params, patch] as const;
}

/** Scroll the first open card into view once, when the page opens with a ?card= link. */
function useScrollToCardOnce(elementId: string | undefined) {
  useEffect(() => {
    if (!elementId) return;
    document.getElementById(elementId)?.scrollIntoView({ block: 'start' });
    // only when the tab opens, not on every toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

interface StepDef {
  id: string;
  title: string;
  description: string;
  link: string;
  required: boolean;
  status: Status;
  detail?: string;
}

interface PhaseDef {
  id: string;
  name: string;
  icon: React.ElementType;
  steps: StepDef[];
}

function StatusIcon({ status }: { status: Status }) {
  if (status === 'loading') return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  if (status === 'complete') return <CheckCircle2 className="h-5 w-5 text-success-text" />;
  if (status === 'incomplete') return <AlertCircle className="h-5 w-5 text-warning-text" />;
  if (status === 'optional') return <Circle className="h-5 w-5 text-muted-foreground" />;
  return <Circle className="h-5 w-5 text-muted-foreground/30" />;
}

function StepCard({ step }: { step: StepDef }) {
  const { t } = useTranslation('taxGuide');
  const border =
    step.status === 'complete' ? 'border-success/30' :
    step.status === 'incomplete' ? 'border-warning/30' :
    'border-border';
  const bg =
    step.status === 'complete' ? 'bg-success/10' :
    step.status === 'incomplete' ? 'bg-warning/10' : '';

  return (
    <div className={`flex items-center gap-4 rounded-lg border px-4 py-3 transition-all ${border} ${bg}`}>
      <div className="shrink-0"><StatusIcon status={step.status} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-sm font-medium ${step.status === 'complete' ? 'text-foreground' : ''}`}>{step.title}</p>
          {!step.required && <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{t('stepCard.optional')}</span>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.description}</p>
        {step.detail && step.status !== 'complete' && step.status !== 'na' && (
          <p className="text-xs text-muted-foreground/70 mt-0.5 italic">{step.detail}</p>
        )}
      </div>
      {step.status !== 'na' && (
        <Link to={step.link} className="shrink-0 print:hidden">
          <Button variant="outline" size="sm" className="gap-1 whitespace-nowrap h-8 text-xs">
            {step.status === 'complete' ? t('stepCard.view') : t('stepCard.setUp')}
            <ArrowRight className="h-3 w-3" />
          </Button>
        </Link>
      )}
    </div>
  );
}

/** Minimal shape of a location row used by the setup wizard (T-393: no `any`). */
interface WizardLocation {
  id?: string;
  name?: string;
  isDefault?: boolean;
}

/** Minimal shape of a tax-rate row used by the setup wizard. */
interface WizardTaxRate {
  id?: string;
}

/** Minimal shape of a category row used by the setup wizard. */
interface WizardCategory {
  id?: string;
  taxes?: unknown[];
  skipPaymentMethodTax?: boolean;
}

export function TaxGuidePage() {
  const { t, i18n } = useTranslation('taxGuide');
  const { hasPermission } = useAuthStore();
  const queryClient = useQueryClient();
  const canReadFbr = hasPermission('fbr:read') || hasPermission('fbr:*') || hasPermission('*');
  const canReadUsers = hasPermission('users:read') || hasPermission('users:*') || hasPermission('*');
  const canReadSettings = hasPermission('settings:update') || hasPermission('*');

  /* ── Parallel data queries ── */
  const { data: tenantData, isLoading: tenantLoading } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then(r => r.data),
    staleTime: 30_000,
  });

  const { data: locationsData, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then(r => r.data),
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['wizard-users-count'],
    queryFn: () => api.get('/users?limit=1&page=1').then(r => r.data),
    enabled: canReadUsers,
  });

  const { data: taxRatesData, isLoading: taxRatesLoading } = useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => api.get('/tax-rates').then(r => r.data),
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data),
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['wizard-products-count'],
    queryFn: () => api.get('/products?limit=1&page=1').then(r => r.data),
  });

  const { data: taxRuleData, isLoading: taxRuleLoading } = useQuery({
    queryKey: ['tenant-tax-rules'],
    queryFn: () => api.get('/tenant/tax-rule-settings').then(r => r.data),
    enabled: canReadSettings,
  });

  const { data: fbrSettingsData, isLoading: fbrLoading } = useQuery({
    queryKey: ['wizard-fbr-settings'],
    queryFn: () => api.get('/tenant/fbr-settings').then(r => r.data),
    enabled: canReadFbr,
  });

  const { data: fbrStatsData } = useQuery({
    queryKey: ['wizard-fbr-stats'],
    queryFn: () => api.get('/fbr/submissions/stats').then(r => r.data),
    enabled: canReadFbr,
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['tenant-current'] });
    invalidateLocationQueries(queryClient);
    queryClient.invalidateQueries({ queryKey: ['wizard-users-count'] });
    queryClient.invalidateQueries({ queryKey: ['tax-rates'] });
    queryClient.invalidateQueries({ queryKey: ['categories'] });
    queryClient.invalidateQueries({ queryKey: ['wizard-products-count'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-tax-rules'] });
    queryClient.invalidateQueries({ queryKey: ['wizard-fbr-settings'] });
    queryClient.invalidateQueries({ queryKey: ['wizard-fbr-stats'] });
  };

  /* ── Derived values ── */
  const locationsList = useMemo<WizardLocation[]>(() => Array.isArray(locationsData) ? locationsData : (locationsData?.data ?? []), [locationsData]);
  const usersTotal: number = usersData?.meta?.total ?? usersData?.total ?? 0;
  const taxRatesList = useMemo<WizardTaxRate[]>(() => Array.isArray(taxRatesData) ? taxRatesData : (taxRatesData?.data ?? []), [taxRatesData]);
  const categoriesList = useMemo<WizardCategory[]>(() => Array.isArray(categoriesData) ? categoriesData : (categoriesData?.data ?? []), [categoriesData]);
  const categoriesWithTax = categoriesList.filter((c: WizardCategory) => (c.taxes?.length ?? 0) > 0);
  const categoriesSkippingPmTax = categoriesList.filter((c: WizardCategory) => c.skipPaymentMethodTax);
  const productsTotal: number = productsData?.meta?.total ?? productsData?.total ?? 0;
  const isTestMode: boolean = tenantData?.testMode ?? false;

  const pmRules = taxRuleData?.paymentMethodTaxRules ?? null;
  const ictEnabled = pmRules && Object.keys(pmRules).length > 0;
  const atlEnabled = taxRuleData?.atlEnabled === true;

  const fbrEnvironment = fbrSettingsData?.fbrEnvironment ?? 'OFF';
  const fbrEnabled = fbrEnvironment !== 'OFF';
  const fbrConfigured = !!(fbrSettingsData?.hasStagingToken || fbrSettingsData?.hasLiveToken) && !!(fbrSettingsData?.fbrNtn);
  const fbrStagingAccepted: number = fbrStatsData?.accepted ?? 0;

  /* ── Build phases ── */
  const phases = useMemo((): PhaseDef[] => [
    {
      id: 'foundation',
      name: t('wizard.phases.foundation'),
      icon: Building2,
      steps: [
        {
          id: 'profile',
          title: t('wizard.steps.profile.title'),
          description: tenantLoading ? t('wizard.checking')
            : tenantData?.name
              ? t('wizard.steps.profile.descRegistered', { name: tenantData.name })
              : t('wizard.steps.profile.descNone'),
          link: '/settings',

          required: true,
          status: tenantLoading ? 'loading' : (tenantData?.name ? 'complete' : 'incomplete'),
          detail: t('wizard.steps.profile.detail'),
        },
        {
          id: 'location',
          title: t('wizard.steps.location.title'),
          description: locationsLoading ? t('wizard.checking')
            : locationsList.length > 0
              ? t(locationsList.length === 1 ? 'wizard.steps.location.descOne' : 'wizard.steps.location.descMany', { n: locationsList.length, name: locationsList.find((l) => l.isDefault)?.name ?? locationsList[0]?.name })
              : t('wizard.steps.location.descNone'),
          link: '/locations',

          required: true,
          status: locationsLoading ? 'loading' : (locationsList.length > 0 ? 'complete' : 'incomplete'),
          detail: t('wizard.steps.location.detail'),
        },
        {
          id: 'team',
          title: t('wizard.steps.team.title'),
          description: usersLoading ? t('wizard.checking')
            : !canReadUsers ? t('wizard.noPermissionToCheck')
            : usersTotal > 1
              ? t('wizard.steps.team.descMany', { n: usersTotal })
              : t('wizard.steps.team.descAdminOnly'),
          link: '/users',

          required: false,
          status: usersLoading ? 'loading'
            : !canReadUsers ? 'na'
            : usersTotal > 1 ? 'complete' : 'optional',
          detail: t('wizard.steps.team.detail'),
        },
      ],
    },
    {
      id: 'catalog',
      name: t('wizard.phases.catalog'),
      icon: Package,
      steps: [
        {
          id: 'tax-rates',
          title: t('wizard.steps.tax-rates.title'),
          description: taxRatesLoading ? t('wizard.checking')
            : taxRatesList.length > 0
              ? t(taxRatesList.length === 1 ? 'wizard.steps.tax-rates.descOne' : 'wizard.steps.tax-rates.descMany', { n: taxRatesList.length })
              : t('wizard.steps.tax-rates.descNone'),
          link: '/tax-rates',

          required: true,
          status: taxRatesLoading ? 'loading' : (taxRatesList.length > 0 ? 'complete' : 'incomplete'),
          detail: t('wizard.steps.tax-rates.detail'),
        },
        {
          id: 'categories',
          title: t('wizard.steps.categories.title'),
          description: categoriesLoading ? t('wizard.checking')
            : categoriesList.length === 0 ? t('wizard.steps.categories.descNone')
            : categoriesWithTax.length === categoriesList.length
              ? t(categoriesList.length === 1 ? 'wizard.steps.categories.descWithTaxOne' : 'wizard.steps.categories.descWithTaxMany', { with: categoriesWithTax.length, total: categoriesList.length })
            : categoriesWithTax.length > 0
              ? t('wizard.steps.categories.descPartial', { with: categoriesWithTax.length, total: categoriesList.length, missing: categoriesList.length - categoriesWithTax.length })
              : t(categoriesList.length === 1 ? 'wizard.steps.categories.descNoTaxOne' : 'wizard.steps.categories.descNoTaxMany', { n: categoriesList.length }),
          link: '/categories',

          required: true,
          status: categoriesLoading ? 'loading'
            : categoriesList.length === 0 ? 'incomplete'
            : categoriesWithTax.length === categoriesList.length ? 'complete' : 'incomplete',
          detail: t('wizard.steps.categories.detail'),
        },
        {
          id: 'products',
          title: t('wizard.steps.products.title'),
          description: productsLoading ? t('wizard.checking')
            : productsTotal > 0
              ? t(productsTotal === 1 ? 'wizard.steps.products.descOne' : 'wizard.steps.products.descMany', { n: productsTotal })
              : t('wizard.steps.products.descNone'),
          link: '/products',

          required: true,
          status: productsLoading ? 'loading' : (productsTotal > 0 ? 'complete' : 'incomplete'),
          detail: t('wizard.steps.products.detail'),
        },
        {
          id: 'templates',
          title: t('wizard.steps.templates.title'),
          description: productsLoading ? t('wizard.checking')
            : productsTotal >= 10
              ? t('wizard.steps.templates.descReady', { n: productsTotal })
              : t('wizard.steps.templates.descDefault'),
          link: '/product-templates',

          required: false,
          status: productsLoading ? 'loading'
            : productsTotal >= 10 ? 'complete' : 'optional',
          detail: t('wizard.steps.templates.detail'),
        },
      ],
    },
    {
      id: 'tax',
      name: t('wizard.phases.tax'),
      icon: Percent,
      steps: [
        {
          id: 'ict',
          title: t('wizard.steps.ict.title'),
          description: taxRuleLoading ? t('wizard.checking')
            : !canReadSettings ? t('wizard.settingsPermissionRequired')
            : ictEnabled
              ? t('wizard.steps.ict.descEnabled')
              : t('wizard.steps.ict.descDisabled'),
          link: '/settings?tab=tax',

          required: false,
          status: taxRuleLoading ? 'loading'
            : !canReadSettings ? 'na'
            : ictEnabled ? 'complete' : 'optional',
          detail: t('wizard.steps.ict.detail'),
        },
        {
          id: 'ict-packaged',
          title: t('wizard.steps.ict-packaged.title'),
          description: !ictEnabled ? t('wizard.steps.ict-packaged.descEnableFirst')
            : categoriesLoading ? t('wizard.checking')
            : categoriesSkippingPmTax.length > 0
              ? t(categoriesSkippingPmTax.length === 1 ? 'wizard.steps.ict-packaged.descFlaggedOne' : 'wizard.steps.ict-packaged.descFlaggedMany', { n: categoriesSkippingPmTax.length })
              : t('wizard.steps.ict-packaged.descNone'),
          link: '/categories',

          required: false,
          status: !ictEnabled ? 'na'
            : categoriesLoading ? 'loading'
            : categoriesSkippingPmTax.length > 0 ? 'complete' : 'optional',
          detail: t('wizard.steps.ict-packaged.detail'),
        },
        {
          id: 'atl',
          title: t('wizard.steps.atl.title'),
          description: taxRuleLoading ? t('wizard.checking')
            : !canReadSettings ? t('wizard.settingsPermissionRequired')
            : atlEnabled
              ? t('wizard.steps.atl.descEnabled')
              : t('wizard.steps.atl.descDisabled'),
          link: '/settings?tab=tax',

          required: false,
          status: taxRuleLoading ? 'loading'
            : !canReadSettings ? 'na'
            : atlEnabled ? 'complete' : 'optional',
          detail: t('wizard.steps.atl.detail'),
        },
      ],
    },
    {
      id: 'fbr',
      name: t('wizard.phases.fbr'),
      icon: Zap,
      steps: [
        {
          id: 'fbr-credentials',
          title: t('wizard.steps.fbr-credentials.title'),
          description: fbrLoading ? t('wizard.checking')
            : !canReadFbr ? t('wizard.fbrPermissionRequired')
            : fbrConfigured
              ? t('wizard.steps.fbr-credentials.descConfigured', { env: fbrEnvironment })
              : fbrSettingsData
                ? t('wizard.steps.fbr-credentials.descNotConfigured', { env: fbrEnvironment })
                : t('wizard.steps.fbr-credentials.descNotLoaded'),
          link: '/fbr',

          required: false,
          status: fbrLoading ? 'loading'
            : !canReadFbr ? 'na'
            : fbrConfigured ? 'complete' : 'optional',
          detail: t('wizard.steps.fbr-credentials.detail'),
        },
        {
          id: 'fbr-hs-codes',
          title: t('wizard.steps.fbr-hs-codes.title'),
          description: !fbrEnabled ? t('wizard.enableFbrFirst')
            : t('wizard.steps.fbr-hs-codes.description'),
          link: '/products',

          required: false,
          status: !fbrEnabled ? 'na' : 'optional',
          detail: t('wizard.steps.fbr-hs-codes.detail'),
        },
        {
          id: 'fbr-staging-test',
          title: t('wizard.steps.fbr-staging-test.title'),
          description: !fbrEnabled ? t('wizard.enableFbrFirst')
            : !canReadFbr ? t('wizard.fbrPermissionRequired')
            : fbrStagingAccepted > 0
              ? t('wizard.steps.fbr-staging-test.descAccepted', { n: fbrStagingAccepted })
              : t('wizard.steps.fbr-staging-test.descNone'),
          link: '/fbr',

          required: false,
          status: !fbrEnabled ? 'na'
            : !canReadFbr ? 'na'
            : fbrStagingAccepted > 0 ? 'complete' : 'optional',
          detail: t('wizard.steps.fbr-staging-test.detail'),
        },
      ],
    },
    {
      id: 'production',
      name: t('wizard.phases.production'),
      icon: Factory,
      steps: [
        {
          id: 'production-recipes',
          title: t('wizard.steps.production-recipes.title'),
          description: t('wizard.steps.production-recipes.description'),
          link: '/recipes',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.production-recipes.detail'),
        },
        {
          id: 'production-product-types',
          title: t('wizard.steps.production-product-types.title'),
          description: t('wizard.steps.production-product-types.description'),
          link: '/products',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.production-product-types.detail'),
        },
        {
          id: 'production-first-run',
          title: t('wizard.steps.production-first-run.title'),
          description: t('wizard.steps.production-first-run.description'),
          link: '/production',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.production-first-run.detail'),
        },
      ],
    },
    {
      id: 'restaurant',
      name: t('wizard.phases.restaurant'),
      icon: UtensilsCrossed,
      steps: [
        {
          id: 'restaurant-setup',
          title: t('wizard.steps.restaurant-setup.title'),
          description: t('wizard.steps.restaurant-setup.description'),
          link: '/restaurant/setup',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-setup.detail'),
        },
        {
          id: 'restaurant-kds',
          title: t('wizard.steps.restaurant-kds.title'),
          description: t('wizard.steps.restaurant-kds.description'),
          link: '/restaurant/setup',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-kds.detail'),
        },
        {
          id: 'restaurant-modifiers',
          title: t('wizard.steps.restaurant-modifiers.title'),
          description: t('wizard.steps.restaurant-modifiers.description'),
          link: '/admin/master-data/menu-modifiers',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-modifiers.detail'),
        },
        {
          id: 'restaurant-service-charge',
          title: t('wizard.steps.restaurant-service-charge.title'),
          description: t('wizard.steps.restaurant-service-charge.description'),
          link: '/settings?tab=team',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-service-charge.detail'),
        },
        {
          id: 'restaurant-line-discount',
          title: t('wizard.steps.restaurant-line-discount.title'),
          description: t('wizard.steps.restaurant-line-discount.description'),
          link: '/restaurant/orders',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-line-discount.detail'),
        },
        {
          id: 'restaurant-subscription',
          title: t('wizard.steps.restaurant-subscription.title'),
          description: t('wizard.steps.restaurant-subscription.description'),
          link: '/settings?tab=team#my-plan',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-subscription.detail'),
        },
        {
          id: 'restaurant-car-takeaway',
          title: t('wizard.steps.restaurant-car-takeaway.title'),
          description: t('wizard.steps.restaurant-car-takeaway.description'),
          link: '/restaurant/takeaway',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.restaurant-car-takeaway.detail'),
        },
      ],
    },
    {
      id: 'payroll',
      name: t('wizard.phases.payroll'),
      icon: DollarSign,
      steps: [
        {
          id: 'payroll-addon',
          title: t('wizard.steps.payroll-addon.title'),
          description: t('wizard.steps.payroll-addon.description'),
          link: '/settings?tab=team#my-plan',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.payroll-addon.detail'),
        },
        {
          id: 'payroll-time-attendance',
          title: t('wizard.steps.payroll-time-attendance.title'),
          description: t('wizard.steps.payroll-time-attendance.description'),
          link: '/time-attendance',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.payroll-time-attendance.detail'),
        },
        {
          id: 'payroll-generate',
          title: t('wizard.steps.payroll-generate.title'),
          description: t('wizard.steps.payroll-generate.description'),
          link: '/payroll',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.payroll-generate.detail'),
        },
      ],
    },
    {
      id: 'advanced',
      name: t('wizard.phases.advanced'),
      icon: Star,
      steps: [
        {
          id: 'loyalty',
          title: t('wizard.steps.loyalty.title'),
          description: t('wizard.steps.loyalty.description'),
          link: '/customers/loyalty',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.loyalty.detail'),
        },
        {
          id: 'shifts',
          title: t('wizard.steps.shifts.title'),
          description: t('wizard.steps.shifts.description'),
          link: '/sell/shifts',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.shifts.detail'),
        },
        {
          id: 'multi-currency',
          title: t('wizard.steps.multi-currency.title'),
          description: t('wizard.steps.multi-currency.description'),
          link: '/settings?tab=integrations',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.multi-currency.detail'),
        },
        {
          id: 'gift-cards',
          title: t('wizard.steps.gift-cards.title'),
          description: t('wizard.steps.gift-cards.description'),
          link: '/sell/gift-cards',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.gift-cards.detail'),
        },
        {
          id: 'customer-portal',
          title: t('wizard.steps.customer-portal.title'),
          description: t('wizard.steps.customer-portal.description'),
          link: '/settings?tab=team',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.customer-portal.detail'),
        },
        {
          id: 'sms-receipts',
          title: t('wizard.steps.sms-receipts.title'),
          description: t('wizard.steps.sms-receipts.description'),
          link: '/settings?tab=team',

          required: false,
          status: 'optional' as Status,
          detail: t('wizard.steps.sms-receipts.detail'),
        },
      ],
    },
    {
      id: 'go-live',
      name: t('wizard.phases.go-live'),
      icon: Rocket,
      steps: [
        {
          id: 'production-mode',
          title: t('wizard.steps.production-mode.title'),
          description: tenantLoading ? t('wizard.checking')
            : isTestMode
              ? t('wizard.steps.production-mode.descTest')
              : t('wizard.steps.production-mode.descLive'),
          link: '/settings',

          required: true,
          status: tenantLoading ? 'loading' : (!isTestMode ? 'complete' : 'incomplete'),
          detail: t('wizard.steps.production-mode.detail'),
        },
      ],
    },
  ], [
    tenantData, tenantLoading,
    locationsList, locationsLoading,
    usersTotal, usersLoading, canReadUsers,
    taxRatesList, taxRatesLoading,
    categoriesList, categoriesLoading, categoriesWithTax, categoriesSkippingPmTax,
    productsTotal, productsLoading,
    taxRuleLoading, canReadSettings, ictEnabled, atlEnabled,
    fbrSettingsData, fbrLoading, canReadFbr, fbrEnabled, fbrConfigured, fbrEnvironment, fbrStagingAccepted,
    isTestMode, t,
  ]);

  /* ── Progress metrics ── */
  const allSteps = phases.flatMap(p => p.steps).filter(s => s.status !== 'na');
  const doneSteps = allSteps.filter(s => s.status === 'complete');
  const requiredSteps = allSteps.filter(s => s.required);
  const requiredDone = requiredSteps.filter(s => s.status === 'complete');
  const pct = allSteps.length > 0 ? Math.round((doneSteps.length / allSteps.length) * 100) : 0;
  const allRequiredDone = requiredDone.length === requiredSteps.length;
  const isLoading = allSteps.some(s => s.status === 'loading');

  const [searchParams, setSearchParams] = useSearchParams();
  const tab: GuideTab = tabFromParam(searchParams.get('tab'));
  const setTab = (key: GuideTab) => setSearchParams(key === 'wizard' ? {} : { tab: TAB_PARAM[key] }, { replace: true });

  // POS-I209: while printing, every card of the current tab is expanded and the page prints on a light theme
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    let wasDark = false;
    const before = () => {
      wasDark = document.documentElement.classList.contains('dark');
      if (wasDark) document.documentElement.classList.remove('dark');
      flushSync(() => setPrintAll(true));
    };
    const after = () => {
      if (wasDark) document.documentElement.classList.add('dark');
      setPrintAll(false);
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);
  const printTabName = t(tab === 'wizard' ? 'tabs.wizard' : tab === 'manual' ? 'tabs.manual' : 'tabs.fbrScenarios');
  const printDate = new Date().toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="store-guide-print space-y-6 page-enter print:bg-background print:text-foreground">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-md">
              <BookOpen className="h-5 w-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              {t('header.title')}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('header.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          {tab === 'wizard' && (
            <Button variant="outline" size="sm" onClick={refreshAll} className="gap-2 shrink-0" disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              {t('header.refresh')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-2 shrink-0">
            <Printer className="h-4 w-4" />
            {t('print.button')}
          </Button>
        </div>
      </div>

      {/* Title block: only on paper */}
      <div className="hidden print:block text-start" data-testid="guide-print-title">
        <p className="text-lg font-bold">{t('print.title', { tab: printTabName })}</p>
        {tenantData?.name && <p className="text-sm">{tenantData.name}</p>}
        <p className="text-xs">{t('print.printed', { date: printDate })}</p>
      </div>

      <div className="print:hidden">
      <TabBar
        tabs={[
          { key: 'wizard', label: t('tabs.wizard'), icon: CheckCircle2 },
          { key: 'manual', label: t('tabs.manual'), icon: BookOpen },
          { key: 'fbr-scenarios', label: t('tabs.fbrScenarios'), icon: Receipt },
        ]}
        activeTab={tab}
        onTabChange={(key) => setTab(key as GuideTab)}
      />
      </div>

      {/* ── MODE BANNER — always visible on Setup Wizard tab ── */}
      {tab === 'wizard' && !tenantLoading && (
        <div className={cn(
          'flex items-start gap-3 rounded-xl border px-4 py-3',
          isTestMode
            ? 'border-warning/30 bg-warning/10'
            : 'border-success/30 bg-success/10',
        )}>
          {isTestMode
            ? <FlaskConical className="h-5 w-5 text-warning-text shrink-0 mt-0.5" />
            : <ShieldCheck className="h-5 w-5 text-success-text shrink-0 mt-0.5" />
          }
          <div className="flex-1 min-w-0">
            <p className={cn(
              'text-sm font-semibold',
              isTestMode ? 'text-warning-text' : 'text-success-text',
            )}>
              {isTestMode ? t('mode.testTitle') : t('mode.prodTitle')}
            </p>
            <p className={cn(
              'text-xs mt-0.5',
              isTestMode ? 'text-warning-text' : 'text-success-text',
            )}>
              {isTestMode ? t('mode.testBody') : t('mode.prodBody')}
            </p>
          </div>
          <Link to="/settings" className="shrink-0 self-center">
            <button className={cn(
              'text-xs font-medium rounded-lg px-3 py-1.5 border transition-colors',
              isTestMode
                ? 'border-warning/30 text-warning-text hover:bg-warning/10'
                : 'border-success/30 text-success-text hover:bg-success/10',
            )}>
              {isTestMode ? t('mode.switchToProd') : t('mode.viewSettings')}
            </button>
          </Link>
        </div>
      )}

      {/* ── SETUP WIZARD TAB ── */}
      {tab === 'wizard' && (
        <div className="space-y-6">
          {/* Overall progress */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-base">
                    {t('progress.stepsComplete', { done: doneSteps.length, total: allSteps.length })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('progress.requiredOptional', { reqDone: requiredDone.length, reqTotal: requiredSteps.length, optDone: doneSteps.length - requiredDone.length, optTotal: allSteps.length - requiredSteps.length })}
                  </p>
                </div>
                <span className="text-3xl font-bold text-primary-text">{pct}%</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${pct === 100 ? 'bg-success' : 'bg-gradient-to-r from-primary to-primary/80'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {allRequiredDone && pct < 100 && (
                <p className="mt-2 text-xs text-success-text flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t('progress.allRequiredDone')}
                </p>
              )}
              {pct === 100 && (
                <p className="mt-2 text-sm font-semibold text-success-text flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('progress.fullyConfigured')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Phases */}
          {phases.map(phase => {
            const PhaseIcon = phase.icon;
            const visible = phase.steps.filter(s => s.status !== 'na');
            if (visible.length === 0) return null;
            const phaseDone = visible.filter(s => s.status === 'complete').length;
            const phaseComplete = phaseDone === visible.length;

            return (
              <div key={phase.id} className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <PhaseIcon className={`h-4 w-4 ${phaseComplete ? 'text-success-text' : 'text-muted-foreground'}`} />
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{phase.name}</h2>
                  <span className="text-xs text-muted-foreground/70 ms-auto">{phaseDone}/{visible.length}</span>
                </div>
                <div className="space-y-1.5">
                  {visible.map(step => <StepCard key={step.id} step={step} />)}
                </div>
              </div>
            );
          })}

          {/* Legend */}
          <div className="flex flex-wrap gap-5 text-xs text-muted-foreground border-t pt-4">
            <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success-text" /> {t('legend.complete')}</div>
            <div className="flex items-center gap-1.5"><AlertCircle className="h-4 w-4 text-warning-text" /> {t('legend.actionNeeded')}</div>
            <div className="flex items-center gap-1.5"><Circle className="h-4 w-4 text-muted-foreground" /> {t('legend.optional')}</div>
            <div className="ms-auto">{t('legend.autoDetected')}</div>
          </div>
        </div>
      )}

      {/* ── USER MANUAL TAB ── */}
      {tab === 'manual' && <UserManual printAll={printAll} />}

      {/* ── FBR SCENARIOS TAB ── */}
      {tab === 'fbr-scenarios' && <FbrScenariosGuide printAll={printAll} />}

      {/* POS-I240: the printed Store Guide ends with the product line (paper only) */}
      <PoweredBy className="hidden print:block" />
    </div>
  );
}

// ─── User Manual ──────────────────────────────────────────────────────────────

interface ManualFeature {
  id: string;
  icon: React.ElementType;
  gradient: string;
  iconBg: string;
  link: string;
  category: 'core' | 'finance' | 'advanced' | 'restaurant';
}

/** Localized copy for one manual module (taxGuide.json -> manual.features.<id>). */
interface ManualFeatureCopy {
  title: string;
  subtitle: string;
  steps: string[];
  tips: string[];
}

function useFeatureCopy(): (id: string) => ManualFeatureCopy {
  const { t } = useTranslation('taxGuide');
  return (id: string) => ({
    title: t(`manual.features.${id}.title`),
    subtitle: t(`manual.features.${id}.subtitle`),
    steps: t(`manual.features.${id}.steps`, { returnObjects: true }) as unknown as string[],
    tips: t(`manual.features.${id}.tips`, { returnObjects: true, defaultValue: [] }) as unknown as string[],
  });
}

const MANUAL_FEATURES: ManualFeature[] = [
  { id: 'dashboard', icon: LayoutDashboard, gradient: 'from-info to-info/80', iconBg: 'bg-info/10', link: '/dashboard', category: 'core' },
  { id: 'pos', icon: ShoppingCart, gradient: 'from-success to-success/80', iconBg: 'bg-success/10', link: '/pos', category: 'core' },
  { id: 'products', icon: Package, gradient: 'from-primary to-primary/80', iconBg: 'bg-primary/10', link: '/products', category: 'core' },
  { id: 'inventory', icon: Warehouse, gradient: 'from-warning to-warning/80', iconBg: 'bg-warning/10', link: '/inventory', category: 'core' },
  { id: 'purchases', icon: Truck, gradient: 'from-destructive to-primary/80', iconBg: 'bg-destructive/10', link: '/purchases', category: 'core' },
  { id: 'locations', icon: MapPin, gradient: 'from-success to-success/80', iconBg: 'bg-success/10', link: '/locations', category: 'core' },
  { id: 'customers', icon: Users, gradient: 'from-info to-info/80', iconBg: 'bg-info/10', link: '/customers', category: 'core' },
  { id: 'expenses', icon: CreditCard, gradient: 'from-destructive to-destructive/80', iconBg: 'bg-destructive/10', link: '/expenses', category: 'finance' },
  { id: 'accounting', icon: BarChart3, gradient: 'from-primary to-info/80', iconBg: 'bg-primary/10', link: '/accounting', category: 'finance' },
  { id: 'reports', icon: BarChart3, gradient: 'from-info to-info/80', iconBg: 'bg-info/10', link: '/reports', category: 'finance' },
  { id: 'bank', icon: Landmark, gradient: 'from-success to-info/80', iconBg: 'bg-success/10', link: '/finance/bank-accounts', category: 'finance' },
  { id: 'promotions', icon: Tag, gradient: 'from-primary to-primary/80', iconBg: 'bg-primary/10', link: '/promotions', category: 'core' },
  { id: 'users', icon: Shield, gradient: 'from-muted-foreground to-muted-foreground/80', iconBg: 'bg-muted', link: '/users', category: 'core' },
  { id: 'fbr', icon: Receipt, gradient: 'from-warning to-warning/80', iconBg: 'bg-warning/10', link: '/fbr', category: 'finance' },
  { id: 'tax', icon: Percent, gradient: 'from-primary to-primary/80', iconBg: 'bg-primary/10', link: '/tax-rates', category: 'core' },
  { id: 'settings', icon: Settings, gradient: 'from-muted-foreground to-muted-foreground/80', iconBg: 'bg-muted', link: '/settings', category: 'core' },
  { id: 'restaurant', icon: UtensilsCrossed, gradient: 'from-warning to-warning/80', iconBg: 'bg-warning/10', link: '/restaurant', category: 'restaurant' },
  { id: 'recipes', icon: BookOpen, gradient: 'from-warning to-warning/80', iconBg: 'bg-warning/10', link: '/recipes', category: 'advanced' },
  { id: 'production', icon: Factory, gradient: 'from-primary to-primary/80', iconBg: 'bg-primary/10', link: '/production', category: 'advanced' },
  { id: 'loyalty', icon: Star, gradient: 'from-warning to-warning/80', iconBg: 'bg-warning/10', link: '/loyalty', category: 'advanced' },
  { id: 'gift-cards', icon: Gift, gradient: 'from-primary to-destructive/80', iconBg: 'bg-primary/10', link: '/gift-cards', category: 'advanced' },
  { id: 'shifts', icon: Clock, gradient: 'from-success to-info/80', iconBg: 'bg-success/10', link: '/shifts', category: 'advanced' },
  { id: 'returns', icon: RotateCcw, gradient: 'from-warning to-warning/80', iconBg: 'bg-warning/10', link: '/sales', category: 'core' },
  { id: 'khata', icon: BookOpen, gradient: 'from-success to-success/80', iconBg: 'bg-success/10', link: '/customers', category: 'advanced' },
  { id: 'cheques', icon: Landmark, gradient: 'from-muted-foreground to-muted-foreground/80', iconBg: 'bg-muted', link: '/finance/cheques', category: 'finance' },
  { id: 'serials', icon: Barcode, gradient: 'from-primary to-primary/80', iconBg: 'bg-primary/10', link: '/inventory/serial-numbers', category: 'advanced' },
  { id: 'expiry', icon: Calendar, gradient: 'from-destructive to-destructive/80', iconBg: 'bg-destructive/10', link: '/inventory', category: 'advanced' },
  { id: 'price-tiers', icon: Layers, gradient: 'from-info to-info/80', iconBg: 'bg-info/10', link: '/products', category: 'advanced' },
  { id: 'multi-currency', icon: DollarSign, gradient: 'from-success to-success/80', iconBg: 'bg-success/10', link: '/settings', category: 'advanced' },
  { id: 'offline', icon: WifiOff, gradient: 'from-muted-foreground to-muted-foreground/80', iconBg: 'bg-muted', link: '/pos', category: 'core' },
  { id: 'customer-portal', icon: Globe, gradient: 'from-primary to-info/80', iconBg: 'bg-primary/10', link: '/settings', category: 'advanced' },
  { id: 'attendance', icon: Clock, gradient: 'from-primary to-primary/80', iconBg: 'bg-primary/10', link: '/people/attendance', category: 'advanced' },
  { id: 'payroll', icon: DollarSign, gradient: 'from-success to-success/80', iconBg: 'bg-success/10', link: '/payroll', category: 'advanced' },
  { id: 'delivery', icon: Truck, gradient: 'from-destructive to-primary/80', iconBg: 'bg-destructive/10', link: '/sell/delivery', category: 'advanced' },
];

const MANUAL_IDS = MANUAL_FEATURES.map((f) => f.id);

function ManualCard({ feature, open, onToggle }: { feature: ManualFeature; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('taxGuide');
  const copyOf = useFeatureCopy();
  const copy = copyOf(feature.id);
  const Icon = feature.icon;
  const headerId = `manual-header-${feature.id}`;
  const panelId = `manual-panel-${feature.id}`;

  const copyLink = async () => {
    const url = buildCardLink(window.location.origin, window.location.pathname, 'manual', feature.id);
    try {
      await navigator.clipboard.writeText(url);
      notify.info(t('share.linkCopied'));
    } catch {
      notify.warning(url);
    }
  };

  return (
    <div
      id={`manual-${feature.id}`}
      data-testid="manual-card"
      className={cn(
        'group scroll-mt-24 rounded-2xl border bg-card overflow-hidden transition-all duration-300 print:break-inside-avoid print:shadow-none',
        'hover:shadow-lg hover:-translate-y-0.5',
        open ? 'shadow-lg' : '',
      )}
    >
      {/* Card header */}
      <div className="relative">
        <button
          id={headerId}
          aria-expanded={open}
          aria-controls={panelId}
          className="w-full text-start p-5 flex items-start gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl"
          onClick={onToggle}
        >
          {/* Icon */}
          <div className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110',
            feature.iconBg,
          )}>
            <div className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br',
              feature.gradient,
            )}>
              <Icon className="h-4 w-4 text-primary-foreground" />
            </div>
          </div>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base leading-tight">{copy.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{copy.subtitle}</p>
          </div>

          {/* Chevron */}
          <ChevronRight className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-300 shrink-0 mt-1',
            open ? 'rotate-90' : '',
          )} />
        </button>
        {open && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute end-11 top-3 h-8 w-8 text-muted-foreground print:hidden"
            onClick={copyLink}
            aria-label={t('share.copyLink')}
            title={t('share.copyLink')}
          >
            <Link2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Expanded content */}
      <div id={panelId} role="region" aria-labelledby={headerId} hidden={!open}>
        {open && (
          <div className="border-t mx-5 pb-5 pt-4 space-y-4 animate-fade-in-up">
            {/* Steps */}
            <div className="space-y-2.5">
              {copy.steps.map((text, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-primary-foreground text-[10px] font-bold',
                    feature.gradient,
                  )}>
                    {idx + 1}
                  </div>
                  <p className="text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            {/* Tips */}
            {copy.tips.length > 0 && (
              <div className="rounded-lg bg-muted/60 border border-muted p-3 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('manual.tipsHeading')}</p>
                {copy.tips.map((tip, i) => (
                  <p key={i} className="text-xs text-muted-foreground leading-relaxed">{tip}</p>
                ))}
              </div>
            )}

            {/* Link */}
            <Link to={feature.link}>
              <Button size="sm" variant="outline" className="gap-1.5 mt-1">
                <ArrowRight className="h-3 w-3" />
                {t('manual.openFeature', { title: copy.title })}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const CATEGORY_COLORS: Record<ManualFeature['category'], string> = {
  core:       'text-info-text bg-info/10 border-info/30',
  finance:    'text-success-text bg-success/10 border-success/30',
  advanced:   'text-primary-text bg-primary/10 border-primary/30',
  restaurant: 'text-warning-text bg-warning/10 border-warning/30',
};

const CATEGORY_ORDER: ManualFeature['category'][] = ['core', 'finance', 'advanced', 'restaurant'];

function UserManual({ printAll }: { printAll: boolean }) {
  const { t } = useTranslation('taxGuide');
  const copyOf = useFeatureCopy();
  const categoryLabel = (cat: ManualFeature['category']) => t(`manual.categories.${cat}`);
  const [params, patch] = useGuideParams();
  // the text box keeps its own state (the router updates in a transition); the URL follows it
  const [search, setSearchText] = useState(() => cleanQuery(params.get('q')));
  const setSearch = (value: string) => { setSearchText(value); patch({ q: value || null }); };
  const activeCategory = chipFromParam<ManualFeature['category'] | 'all'>(params.get('cat'), ['all', ...CATEGORY_ORDER], 'all');
  const setActiveCategory = (cat: ManualFeature['category'] | 'all') => patch({ cat: cat === 'all' ? null : cat });
  const openIds = cardsFromParam(params.get('card'), MANUAL_IDS);
  const toggleCard = (id: string) => {
    const next = openIds.includes(id) ? openIds.filter((x) => x !== id) : [...openIds, id];
    patch({ card: next.length > 0 ? next.join(',') : null });
  };
  useScrollToCardOnce(openIds[0] ? `manual-${openIds[0]}` : undefined);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return MANUAL_FEATURES.filter(f => {
      const c = copyOf(f.id);
      const matchesSearch = !q || c.title.toLowerCase().includes(q) || c.subtitle.toLowerCase().includes(q) ||
        c.steps.some(s => s.toLowerCase().includes(q)) || c.tips.some(s => s.toLowerCase().includes(q));
      const matchesCat = activeCategory === 'all' || f.category === activeCategory;
      return matchesSearch && matchesCat;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeCategory, t]);

  // The Quick Reference rows and the FBR scenarios are searched too (POS-I208)
  const taxRows = t('manual.taxRef.rows', { returnObjects: true }) as unknown as { type: string; payment: string; rate: string; setting: string }[];
  const query = search.toLowerCase().trim();
  const visibleTaxRows = query
    ? taxRows.filter((row) => [row.type, row.payment, row.rate, row.setting].some((text) => text.toLowerCase().includes(query)))
    : taxRows;
  const scenarioMatches = query ? FBR_SCENARIOS.filter((s) => scenarioMatchesQuery(t, s, query)).length : 0;

  const grouped = useMemo(() => {
    const groups: Partial<Record<ManualFeature['category'], ManualFeature[]>> = {};
    for (const cat of CATEGORY_ORDER) {
      const items = filtered.filter(f => f.category === cat);
      if (items.length > 0) groups[cat] = items;
    }
    return groups;
  }, [filtered]);

  return (
    <div className="space-y-6">
      {/* Intro banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary to-primary/80 p-6 text-primary-foreground shadow-xl print:hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background/20 backdrop-blur-sm">
              <BookOpen className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{t('manual.bannerTitle')}</h2>
              <p className="text-xs text-primary-foreground/70">{t('manual.bannerSubtitle', { n: MANUAL_FEATURES.length })}</p>
            </div>
          </div>
          <p className="text-sm text-primary-foreground/80 leading-relaxed max-w-2xl">
            {t('manual.bannerBody')}
          </p>
        </div>
      </div>

      {/* Search + category filter */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center print:hidden">
        <div className="relative w-full shrink-0 xl:w-80">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('manual.searchPlaceholder')}
            className="ps-9 pe-9"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {(['all', ...CATEGORY_ORDER] as const).map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={cn(
                'text-xs px-3 py-1.5 rounded-full border font-medium transition-colors',
                activeCategory === cat
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-card text-muted-foreground border-border hover:border-foreground/50 hover:text-foreground'
              )}
            >
              {cat === 'all' ? t('manual.allCount', { n: MANUAL_FEATURES.length }) : `${categoryLabel(cat)} (${MANUAL_FEATURES.filter(f => f.category === cat).length})`}
            </button>
          ))}
        </div>
      </div>

      {scenarioMatches > 0 && (
        <p className="text-sm print:hidden">
          <Link
            to={{ search: new URLSearchParams({ tab: TAB_PARAM['fbr-scenarios'], q: search.trim() }).toString() }}
            className="inline-flex items-center gap-1.5 font-medium text-primary-text underline underline-offset-2 hover:opacity-80"
          >
            <Receipt className="h-4 w-4" />
            {t(scenarioMatches === 1 ? 'manual.fbrMatchesOne' : 'manual.fbrMatchesMany', { count: scenarioMatches })}
          </Link>
        </p>
      )}

      {/* No results */}
      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="font-medium text-foreground">{t('manual.noResultsTitle')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('manual.noResultsHint')}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => { setSearch(''); setActiveCategory('all'); }}>
            {t('manual.clearFilters')}
          </Button>
        </div>
      )}

      {/* Feature groups */}
      {(CATEGORY_ORDER as ManualFeature['category'][]).map(cat => {
        const items = grouped[cat];
        if (!items) return null;
        return (
          <div key={cat} className="space-y-3">
            <div className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border', CATEGORY_COLORS[cat])}>
              {categoryLabel(cat)}
              <span className="opacity-60">({items.length})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 items-start gap-4 print:grid-cols-1">
              {items.map(feature => (
                <ManualCard key={feature.id} feature={feature} open={printAll || openIds.includes(feature.id)} onToggle={() => toggleCard(feature.id)} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Pakistan tax quick reference */}
      <div className="rounded-2xl border bg-card overflow-hidden print:break-inside-avoid">
        <div className="px-5 pt-5 pb-3 border-b">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-success to-success/80">
              <FileText className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <p className="font-semibold">{t('manual.taxRef.title')}</p>
          </div>
        </div>
        <div className="p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-muted/60">
                  <th className="text-start p-2.5 font-semibold border rounded-ss-lg">{t('manual.taxRef.colBusinessType')}</th>
                  <th className="text-start p-2.5 font-semibold border">{t('manual.taxRef.colPayment')}</th>
                  <th className="text-start p-2.5 font-semibold border">{t('manual.taxRef.colRate')}</th>
                  <th className="text-start p-2.5 font-semibold border rounded-se-lg">{t('manual.taxRef.colSetting')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleTaxRows.length === 0 && (
                  <tr><td colSpan={4} className="p-3 border text-center text-muted-foreground">{t('manual.taxRef.noRows')}</td></tr>
                )}
                {visibleTaxRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 1 ? 'bg-muted/30' : ''}>
                    <td className="p-2.5 border text-foreground">{row.type}</td>
                    <td className="p-2.5 border text-muted-foreground">{row.payment}</td>
                    <td className="p-2.5 border font-mono font-semibold text-primary-text">{row.rate}</td>
                    <td className="p-2.5 border text-muted-foreground">{row.setting}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            <Trans t={t} i18nKey="manual.taxRef.note" components={{ b: <strong /> }} />
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('manual.taxRef.updated', { date: t('manual.taxRef.updatedOn') })}
          </p>
        </div>
      </div>

      {/* Provincial service tax reference (POS-I197) */}
      <ProvincialRatesSection />

      {/* Quick links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 print:hidden">
        {[
          { to: '/tax-rates', label: t('quickLinks.taxRates'), icon: Percent, color: 'text-primary-text' },
          { to: '/categories', label: t('quickLinks.categories'), icon: Tag, color: 'text-warning-text' },
          { to: '/fbr', label: t('quickLinks.fbrSettings'), icon: Receipt, color: 'text-warning-text' },
          { to: '/settings', label: t('quickLinks.storeSettings'), icon: Settings, color: 'text-muted-foreground' },
        ].map(({ to, label, icon: Icon, color }) => (
          <Link key={to} to={to}>
            <div className="flex items-center gap-2 rounded-xl border bg-card p-3 hover:bg-muted/50 transition-colors cursor-pointer">
              <Icon className={cn('h-4 w-4', color)} />
              <span className="text-sm font-medium">{label}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}


// ─── FBR Scenarios Guide ──────────────────────────────────────────────────────

interface ScenarioGuide {
  id: FbrScenarioId;
  /** The official FBR name, the same text as Finance > FBR > Scenario Checklist (POS-I194). */
  name: string;
  category: FbrScenarioCategory;
  /** Neutral card: official name plus the same three-step procedure, no detailed rules. */
  neutral: boolean;
  description: string;
  buyerType?: string;
  rate?: string;
  hsCodeExample?: string;
  uom?: string;
  gradient: string;
  keyRules: string[];
  prerequisites: string[];
  steps: string[];
  warnings?: string[];
  /** Payload field names for integrators, shown in a collapsed line (POS-I203). */
  technical?: string[];
}

const SCENARIO_CATEGORIES = [
  { id: 'all',          labelKey: 'fbr.filters.all' },
  { id: 'standard',     labelKey: 'fbr.filters.standard' },
  { id: 'special-rate', labelKey: 'fbr.filters.specialRate' },
  { id: 'exempt-zero',  labelKey: 'fbr.filters.exemptZero' },
  { id: 'sector',       labelKey: 'fbr.filters.sector' },
  { id: 'pos-tier1',    labelKey: 'fbr.filters.posTier1' },
] as const;

const CATEGORY_GRADIENT: Record<FbrScenarioCategory, string> = {
  'standard': 'from-info to-primary/80',
  'special-rate': 'from-primary to-primary/80',
  'exempt-zero': 'from-success to-info/80',
  'sector': 'from-muted-foreground to-muted-foreground/80',
  'pos-tier1': 'from-destructive to-primary/80',
};

// TODO(T-397 follow-up, needs native/tax-translator review): the nine detailed scenario records below
// (rules and steps) are English-only on purpose, a low-confidence machine translation of tax-legal
// text is worse than English. The other 19 scenarios use the neutral card (translated strings in the
// taxGuide namespace, fbr.neutral.*). POS-I194: names come from the official FBR list
// (lib/fbr-scenario-names.ts); a detailed record keeps only what the app itself ships (18% standard
// rate, Reduced GST 5%, Exempt 0%) and never a statutory citation or rate the app does not carry.
// Detailed records are listed in FBR_DETAILED_SCENARIO_IDS; adding one needs a tax advisor's check.
const DETAILED_SCENARIOS: Record<string, Omit<ScenarioGuide, 'id' | 'name' | 'category' | 'neutral'>> = {
  SN001: {
    description: 'Sale of taxable goods to a buyer registered with FBR. The standard GST rate (18%) applies.',
    buyerType: 'Registered (NTN or CNIC)',
    rate: '18% GST (standard)',
    hsCodeExample: '0402.2990 (Milk Powder), 1901.9090 (Food preparations)',
    uom: 'Pcs / KG / as applicable',
    gradient: 'from-info to-primary/80',
    keyRules: [
      'Buyer is registered with FBR: NTN (7 digits) or CNIC (13 digits for an individual)',
      'No SRO schedule is needed for the standard 18% rate',
      'Buyer type is Registered',
    ],
    prerequisites: [
      'Buyer is registered with FBR: NTN (7 digits) or CNIC (13 digits for an individual)',
      'Check that the buyer is active on FBR\'s Sales Tax Active Taxpayer List.',
      'Customer created in POS with the NTN or CNIC filled in',
      'Products have FBR scenario SN001 and a valid HS Code',
      'FBR NTN and Bearer Token configured in Finance > FBR',
    ],
    steps: [
      'Create a customer record with the business buyer\'s NTN or CNIC',
      'Mark the customer as a "Registered" buyer type',
      'Make sure all products in the sale have HS codes and FBR scenario SN001 on the product',
      'Open POS and attach the registered customer to the sale',
      'Complete checkout; the FBR submission is queued automatically',
      'Monitor the FBR Submissions page; the status should change to ACCEPTED',
    ],
    technical: ['fbrScenarioId = SN001', 'buyerType = Registered', 'extraTax is numeric (0 if not applicable)'],
  },
  SN002: {
    description: 'Sale of taxable goods to a walk-in consumer or an unregistered buyer. The standard GST rate (18%) applies. Most common for retail shops.',
    buyerType: 'Unregistered (CNIC or empty)',
    rate: '18% GST (standard)',
    hsCodeExample: '2106.9099 (Beverages), 3401.1100 (Soap)',
    uom: 'Pcs',
    gradient: 'from-success to-success/80',
    keyRules: [
      'The buyer\'s NTN or CNIC can be empty, or a 13-digit CNIC',
      'For a buyer with an NTN use SN001 instead',
      'Most common scenario for general retail POS',
      'No SRO schedule is needed for the standard rate',
    ],
    prerequisites: [
      'Products have FBR scenario SN002 and a valid HS Code',
      'No NTN required; attaching a customer is optional',
      'FBR NTN and Bearer Token configured',
    ],
    steps: [
      'Add products to the POS cart (products must have HS codes)',
      'Optionally attach a customer (CNIC optional, no NTN)',
      'Complete checkout; the FBR submission is queued automatically',
      'The FBR invoice treats the buyer as Unregistered',
    ],
    technical: ['fbrScenarioId = SN002', 'buyerType = "Unregistered"', 'buyerNTNCNIC is empty or a 13-digit CNIC'],
  },
  SN003: {
    description: 'Supply in the steel melting and re-rolling sector, where the buyer withholds the sales tax.',
    buyerType: 'Registered steel-sector buyer',
    rate: '18% GST, withheld by the buyer',
    hsCodeExample: '7216.1000 (re-rolled steel sections)',
    uom: 'MT (metric tonnes)',
    gradient: 'from-muted-foreground to-muted-foreground/80',
    keyRules: [
      'The buyer withholds the whole sales tax (100%)',
      'The price must not be below FBR\'s notified minimum price for the product',
      'The unit of measure must be MT (metric tonnes)',
      'FBR accepts only the steel HS codes it allows for this scenario and rejects the rest',
    ],
    prerequisites: [
      'Buyer and seller are registered in the steel sector with FBR',
      'FBR\'s notified minimum price is known for each steel product',
      'Products have the correct steel HS codes',
      'FBR scenario SN003 on the product, unit MT',
    ],
    steps: [
      'Set up steel products with their HS codes',
      'Set the notified price at or above FBR\'s minimum (check it on the FBR portal)',
      'Record 100% of the sales tax as withheld at source',
      'The buyer\'s NTN must be a valid steel-sector registration',
      'Complete the sale; FBR validates the minimum price',
    ],
    warnings: [
      'FBR rejects invoices priced below the minimum notified price.',
      'FBR rejects HS codes outside the allowed steel range for this scenario.',
    ],
    technical: [
      'fbrScenarioId = SN003, UOM = MT',
      'salesTaxWithheldAtSource = salesTaxApplicable',
      'fixedNotifiedValueOrRetailPrice at or above the FBR minimum notified price',
    ],
  },
  SN005: {
    description: 'Sale of goods that FBR allows at a reduced GST rate. In the app this is the Reduced GST rate (5%) under Catalog > Tax Rates.',
    buyerType: 'Any',
    rate: '5% GST (reduced)',
    hsCodeExample: '3004.9099 (Medicine), 1209.9900 (Seeds)',
    uom: 'Pcs / KG',
    gradient: 'from-primary to-primary/80',
    keyRules: [
      'The product\'s HS code must be on FBR\'s reduced-rate list; FBR checks it',
      'The reduced rate comes from the tax rate on the product',
      'The SRO schedule is filled in on the invoice automatically from the tax rate',
    ],
    prerequisites: [
      'Check that the HS code qualifies for the reduced rate',
      'The product\'s tax rate is Reduced GST (5%)',
      'FBR scenario SN005 on the product',
    ],
    steps: [
      'Set up the product with its HS code and the Reduced GST tax rate',
      'Complete the sale through POS',
      'The FBR invoice carries the reduced rate and its schedule automatically',
      'FBR checks the HS code against its reduced-rate list',
    ],
    technical: ['fbrScenarioId = SN005', 'sroScheduleNo is set from the tax rate'],
  },
  SN006: {
    description: 'Sale of goods that are exempt from sales tax. No GST is charged (the Exempt 0% rate in Catalog > Tax Rates).',
    buyerType: 'Any',
    rate: '0% (exempt)',
    hsCodeExample: '1006.3000 (Rice), 0701.9000 (Potatoes)',
    uom: 'KG / MT',
    gradient: 'from-success to-info/80',
    keyRules: [
      'No GST is charged on exempt goods',
      'The seller cannot claim input tax credit on exempt sales (unlike zero rated)',
      'No SRO schedule is sent for exempt goods',
      'Check that the goods really are on FBR\'s exempt list',
    ],
    prerequisites: [
      'Check that the HS code is on FBR\'s exempt list',
      'The product\'s tax rate is Exempt (0%)',
      'FBR scenario SN006 on the product',
    ],
    steps: [
      'Set up the product with the Exempt tax rate and no SRO schedule',
      'Check the HS code against FBR\'s exempt list',
      'Process the sale; no tax appears on the receipt',
      'The FBR invoice confirms the exempt status',
    ],
    warnings: [
      'Exempt is not the same as zero rated. For exempt goods the supplier cannot claim input tax credit; for zero rated goods (SN007) input tax credit can be claimed.',
    ],
    technical: ['fbrScenarioId = SN006', 'Do not send sroScheduleNo for exempt goods'],
  },
  SN007: {
    description: 'Sale of goods taxed at 0% where the seller can still claim input tax credit, such as exports.',
    buyerType: 'Any',
    rate: '0% (zero rated)',
    hsCodeExample: '6302.6000 (Textile exports), 4011.1000 (Tyres, export)',
    uom: 'Pcs / KG / MT',
    gradient: 'from-primary to-info/80',
    keyRules: [
      'Input tax credit can be claimed (unlike exempt goods, SN006)',
      'Mainly used by exporters claiming back the tax paid on inputs',
      'Check that the goods really qualify for zero rating',
    ],
    prerequisites: [
      'Check that the HS code qualifies for zero rating',
      'The product\'s tax rate is 0%',
      'FBR scenario SN007 on the product',
    ],
    steps: [
      'Set up the product with the 0% tax rate',
      'Process the export sale or approved zero rated supply',
      'The FBR invoice confirms the zero rated status',
      'File the refund claim for input tax paid on purchases',
    ],
    technical: ['fbrScenarioId = SN007', 'sroScheduleNo is set from the tax rate'],
  },
  SN008: {
    description: 'Goods sold at a retail price printed on the pack. The tax is worked out on that printed retail price.',
    buyerType: 'Any',
    rate: '18% on the printed retail price',
    hsCodeExample: '3401.1100 (Soap), 3301.2900 (Shampoo), 2106.9099 (Packaged food)',
    uom: 'Pcs / Packs',
    gradient: 'from-success to-success/80',
    keyRules: [
      'The tax is calculated on the retail price (MRP) printed on the pack',
      'GST is already inside the printed price: use the INCLUSIVE tax type in POS',
      'Do not add GST on top of the printed price',
      'Check that the goods really belong to the 3rd Schedule',
    ],
    prerequisites: [
      'Check that the product is a 3rd Schedule item',
      'The retail price (MRP) from the pack is entered on the product',
      'Tax type is INCLUSIVE on the product',
      'FBR scenario SN008 on the product',
    ],
    steps: [
      'Set up the product with its retail price (MRP)',
      'Set the tax type to INCLUSIVE (the tax is in the price; do not add it on top)',
      'The sale proceeds at the MRP and POS extracts the tax from the price',
      'The FBR invoice shows the MRP and the GST inside it',
    ],
    warnings: ['Do not add extra GST at POS for these items. The tax is already inside the printed retail price.'],
    technical: [
      'fbrScenarioId = SN008',
      'fixedNotifiedValueOrRetailPrice = MRP printed on the pack',
      'sroScheduleNo = "3rd Schedule goods"',
    ],
  },
  SN024: {
    description: 'Goods named in a government notification (SRO) that carry a special reduced rate.',
    buyerType: 'Any',
    rate: 'Reduced rate set by the notification',
    hsCodeExample: 'As listed in the notification',
    uom: 'As per goods',
    gradient: 'from-destructive to-primary/80',
    keyRules: [
      'The product\'s HS code must be in the notification\'s list',
      'The rate comes from the notification; read its official text for the goods and rates',
      'FBR checks the HS code against the list',
    ],
    prerequisites: [
      'Check that the goods are listed in the notification',
      'The product\'s tax rate is set to the notified rate',
      'FBR scenario SN024 on the product',
    ],
    steps: [
      'Download the notification from the FBR website to find the eligible goods',
      'Set up the product with the matching HS code',
      'Use the notified reduced rate as the product tax rate',
      'Process the sale; FBR validates the HS code against the list',
    ],
    technical: ['fbrScenarioId = SN024', 'sroScheduleNo is set from the tax rate'],
  },
  SN026: {
    description: 'Sale by a retailer to a walk-in consumer. Used by retailers that must connect their POS to FBR.',
    buyerType: 'Unregistered (CNIC or empty)',
    rate: '18% GST (standard retail rate)',
    hsCodeExample: '0402.2990 (Dairy), 3401.1100 (Soap), 2106.9099 (Beverages)',
    uom: 'Pcs',
    gradient: 'from-destructive to-primary/80',
    keyRules: [
      'Retailers that FBR requires to connect their POS use this scenario for walk-in sales',
      'Walk-in consumers: no NTN needed',
      'SN026 is similar to SN002; SN026 is for retail POS sales to end consumers',
    ],
    prerequisites: [
      'Check with FBR whether your business must connect its POS',
      'This system connects the POS to FBR for you',
      'Products have an HS code and FBR scenario SN026',
    ],
    steps: [
      'Set the products\' FBR scenario to SN026',
      'Process walk-in sales through POS',
      'The FBR invoice is submitted automatically',
      'The customer receives the FBR-verified receipt',
    ],
    technical: ['fbrScenarioId = SN026'],
  },
};

// The neutral card: no rule is invented. The official name, the same three-step procedure and the
// advice to confirm rates with a tax advisor (strings: taxGuide fbr.neutral.*).
const FBR_SCENARIOS: ScenarioGuide[] = FBR_SCENARIO_IDS.map((id): ScenarioGuide => {
  const category = scenarioCategory(id);
  const detail = isDetailedScenario(id) ? DETAILED_SCENARIOS[id] : undefined;
  if (detail) return { id, name: FBR_SCENARIO_NAMES[id], category, neutral: false, ...detail };
  return {
    id,
    name: FBR_SCENARIO_NAMES[id],
    category,
    neutral: true,
    description: '',
    gradient: CATEGORY_GRADIENT[category],
    keyRules: [],
    prerequisites: [],
    steps: [],
  };
});

type CategoryFilter = 'all' | 'standard' | 'special-rate' | 'exempt-zero' | 'sector' | 'pos-tier1';

// A neutral card has no stored description: its text comes from the translated template.
function scenarioDescription(t: TFunction<'taxGuide'>, s: ScenarioGuide): string {
  return s.neutral ? t('fbr.neutral.description', { id: s.id, name: s.name }) : s.description;
}

/** The FBR Scenarios tab search, also used by the User Manual to count matching scenarios (POS-I208). */
function scenarioMatchesQuery(t: TFunction<'taxGuide'>, s: ScenarioGuide, query: string): boolean {
  const q = query.toLowerCase().trim();
  return !q || s.id.toLowerCase().includes(q) || s.name.toLowerCase().includes(q) ||
    scenarioDescription(t, s).toLowerCase().includes(q) || (s.buyerType ?? '').toLowerCase().includes(q);
}

function FbrScenariosGuide({ printAll }: { printAll: boolean }) {
  const { t } = useTranslation('taxGuide');
  const [params, patch] = useGuideParams();
  const expanded = cardsFromParam(params.get('card'), FBR_SCENARIO_IDS)[0] ?? null;
  const setExpanded = (id: string | null) => patch({ card: id });
  const [filter, setFilter] = useState<CategoryFilter>('all');
  // the text box keeps its own state (the router updates in a transition); the URL follows it
  const [search, setSearchText] = useState(() => cleanQuery(params.get('q')));
  const setSearch = (value: string) => { setSearchText(value); patch({ q: value || null }); };
  useScrollToCardOnce(expanded ? `fbr-scenario-${expanded}` : undefined);
  const copyLink = async (id: string) => {
    const url = buildCardLink(window.location.origin, window.location.pathname, 'fbr-scenarios', id);
    try {
      await navigator.clipboard.writeText(url);
      notify.info(t('share.linkCopied'));
    } catch {
      notify.warning(url);
    }
  };

  const descriptionOf = (s: ScenarioGuide) => scenarioDescription(t, s);

  const filtered = FBR_SCENARIOS.filter(s => {
    const matchCat = filter === 'all' || s.category === filter;
    return matchCat && scenarioMatchesQuery(t, s, search);
  });

  const catCounts: Record<string, number> = { all: FBR_SCENARIOS.length };
  FBR_SCENARIOS.forEach(s => { catCounts[s.category] = (catCounts[s.category] || 0) + 1; });

  return (
    <div className="space-y-6">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-warning via-warning to-destructive/80 p-6 text-warning-foreground shadow-xl print:hidden">
        <div className="absolute inset-0 opacity-15"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background/20 backdrop-blur-sm">
              <Receipt className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{t('fbr.heroTitle')}</h2>
              <p className="text-xs text-primary-foreground/70">{t('fbr.heroSubtitle')}</p>
            </div>
          </div>
          <p className="text-sm text-primary-foreground/80 leading-relaxed max-w-2xl">
            {t('fbr.heroBody')}
          </p>
        </div>
      </div>

      {/* Prerequisites summary */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-warning-text" />
            <p className="font-semibold text-sm">{t('fbr.prereqTitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'token', label: t('fbr.prereq.token.label'), desc: t('fbr.prereq.token.desc'), link: '/finance/fbr' },
              { key: 'hsCode', label: t('fbr.prereq.hsCode.label'), desc: t('fbr.prereq.hsCode.desc'), link: '/catalog/products' },
              { key: 'scenarioId', label: t('fbr.prereq.scenarioId.label'), desc: t('fbr.prereq.scenarioId.desc'), link: '/catalog/products' },
              { key: 'staging', label: t('fbr.prereq.staging.label'), desc: t('fbr.prereq.staging.desc'), link: '/finance/fbr' },
            ].map(item => (
              <Link key={item.key} to={item.link}>
                <div className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer">
                  <CheckCircle2 className="h-4 w-4 text-success-text shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0 ms-auto" />
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3 print:hidden">
        <FilterChips
          chips={SCENARIO_CATEGORIES.map((cat) => ({
            key: cat.id,
            label: t(cat.labelKey),
            count: catCounts[cat.id] ?? 0,
          }))}
          value={filter}
          onChange={(key) => setFilter(key as CategoryFilter)}
          size="sm"
          wrap
        />

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder={t('fbr.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute end-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs px-1"
            >
              ✕
            </button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {t('fbr.showing', { shown: filtered.length, total: FBR_SCENARIOS.length })}
        </p>
      </div>

      {/* Scenario cards */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="rounded-2xl border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
            {t('fbr.noMatch')}
          </div>
        )}
        {filtered.map(scenario => {
          const isExpanded = printAll || expanded === scenario.id;
          return (
            <div
              key={scenario.id}
              id={`fbr-scenario-${scenario.id}`}
              className={cn(
                'scroll-mt-24 rounded-2xl border bg-card overflow-hidden transition-all duration-300 print:break-inside-avoid print:shadow-none',
                isExpanded ? 'shadow-lg ring-1 ring-inset ring-border/60' : 'hover:shadow-md hover:-translate-y-0.5',
              )}
            >
              <div className="relative">
              <button
                id={`fbr-header-${scenario.id}`}
                aria-expanded={isExpanded}
                aria-controls={`fbr-panel-${scenario.id}`}
                className="w-full text-start p-5 flex items-start gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setExpanded(isExpanded ? null : scenario.id)}
              >
                {/* Scenario badge */}
                <div className={cn(
                  'flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-gradient-to-br text-primary-foreground font-bold shadow-sm',
                  scenario.gradient,
                )}>
                  <span className="text-[10px] opacity-80 font-medium">FBR</span>
                  <span className="text-sm leading-tight">{scenario.id}</span>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-base leading-tight">{scenario.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{descriptionOf(scenario)}</p>
                  {!scenario.neutral && scenario.buyerType && scenario.rate && scenario.uom && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">
                        {scenario.buyerType.length > 30 ? scenario.buyerType.slice(0, 30) + '…' : scenario.buyerType}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-primary/10 text-primary-text px-2 py-0.5 text-[10px] font-medium">
                        {scenario.rate.length > 25 ? scenario.rate.slice(0, 25) + '…' : scenario.rate}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-warning/10 text-warning-text px-2 py-0.5 text-[10px] font-medium">
                        {t('fbr.uomBadge', { uom: scenario.uom.split('/')[0].trim() })}
                      </span>
                    </div>
                  )}
                </div>

                <ChevronRight className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform duration-300 shrink-0 mt-2',
                  isExpanded ? 'rotate-90' : '',
                )} />
              </button>
              {isExpanded && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute end-11 top-3 h-8 w-8 text-muted-foreground print:hidden"
                  onClick={() => copyLink(scenario.id)}
                  aria-label={t('share.copyLink')}
                  title={t('share.copyLink')}
                >
                  <Link2 className="h-4 w-4" />
                </Button>
              )}
              </div>

              <div id={`fbr-panel-${scenario.id}`} role="region" aria-labelledby={`fbr-header-${scenario.id}`} hidden={!isExpanded}>
              {isExpanded && (
                <div className="border-t mx-5 pb-5 pt-4 space-y-4">
                  {/* HS Code + UOM */}
                  {scenario.hsCodeExample && scenario.uom && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="rounded-lg bg-muted/30 p-3">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('fbr.exampleHsCodes')}</p>
                        <p className="text-xs font-mono text-foreground leading-relaxed">{scenario.hsCodeExample}</p>
                      </div>
                      <div className="rounded-lg bg-muted/30 p-3">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('fbr.validUom')}</p>
                        <p className="text-xs font-mono text-foreground">{scenario.uom}</p>
                      </div>
                    </div>
                  )}

                  {/* Key rules */}
                  {scenario.keyRules.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('fbr.keyRules')}</p>
                      <div className="space-y-1.5">
                        {scenario.keyRules.map((rule, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-primary-foreground text-[9px] font-bold mt-0.5',
                              scenario.gradient,
                            )}>✓</span>
                            <p className="text-sm leading-relaxed">{rule}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prerequisites */}
                  {scenario.prerequisites.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('fbr.prerequisites')}</p>
                      <div className="space-y-1.5">
                        {scenario.prerequisites.map((prereq, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5 text-warning-text shrink-0 mt-0.5" />
                            <p className="text-sm">{prereq}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Steps */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('fbr.stepByStep')}</p>
                    <div className="space-y-2">
                      {(scenario.neutral
                        ? [
                            t('fbr.neutral.step1'),
                            t('fbr.neutral.step2'),
                            t('fbr.neutral.step3', { id: scenario.id }),
                          ]
                        : scenario.steps
                      ).map((step, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className={cn(
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-primary-foreground text-[10px] font-bold',
                            scenario.gradient,
                          )}>
                            {i + 1}
                          </div>
                          <p className="text-sm leading-relaxed">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Warnings */}
                  {scenario.warnings && scenario.warnings.length > 0 && (
                    <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 space-y-1.5">
                      <p className="text-xs font-semibold text-warning-text uppercase tracking-wide">{t('fbr.warnings')}</p>
                      {scenario.warnings.map((w, i) => (
                        <p key={i} className="text-xs text-warning-text leading-relaxed">{w}</p>
                      ))}
                    </div>
                  )}

                  {/* Developer keys in one collapsed line (POS-I203) */}
                  {scenario.technical && scenario.technical.length > 0 && (
                    <details className="rounded-lg border">
                      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground">
                        {t('fbr.technicalDetails')}
                      </summary>
                      <ul className="space-y-1 border-t p-3">
                        {scenario.technical.map((item, i) => (
                          <li key={i} className="text-xs font-mono text-foreground" dir="ltr">{item}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <p className="text-xs text-muted-foreground leading-relaxed">{t('fbr.advisorNote')}</p>
                </div>
              )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 print:hidden">
        {[
          { to: '/fbr', label: t('quickLinks.fbrSettings'), icon: Receipt, color: 'text-warning-text' },
          { to: '/products', label: t('quickLinks.productsHs'), icon: Package, color: 'text-primary-text' },
          { to: '/sales', label: t('quickLinks.salesHistory'), icon: ShoppingCart, color: 'text-success-text' },
          { to: '/settings', label: t('quickLinks.storeSettings'), icon: Settings, color: 'text-muted-foreground' },
        ].map(({ to, label, icon: Icon, color }) => (
          <Link key={to} to={to}>
            <div className="flex items-center gap-2 rounded-xl border bg-card p-3 hover:bg-muted/50 transition-colors cursor-pointer">
              <Icon className={cn('h-4 w-4', color)} />
              <span className="text-sm font-medium">{label}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
